<template>
  <!-- hasContent: hides the trigger entirely when the slot renders no visible nodes -->
  <DropdownMenuRoot v-if="hasContent" v-model:open="isOpen" :modal="false">
    <DropdownMenuTrigger as-child>
      <slot name="trigger" :open="isOpen" :toggle="toggle">
        <BaseButton v-bind="buttonProps" />
      </slot>
    </DropdownMenuTrigger>
    <DropdownMenuPortal>
      <DropdownMenuContent
        :align="align"
        :side="side"
        :side-offset="4"
        :class="[
          'dd-content',
          {
            'dd-content--fit': fitContent,
            'dd-content--match-trigger': matchTriggerWidth,
          },
          contentClass,
        ]"
        @click="handleContentClick"
      >
        <slot />
      </DropdownMenuContent>
    </DropdownMenuPortal>
  </DropdownMenuRoot>
  <slot
    v-else-if="!hideWhenEmpty"
    name="trigger"
    :open="isOpen"
    :toggle="toggle"
  >
    <BaseButton v-bind="buttonProps" />
  </slot>
</template>

<script>
import {
  DropdownMenuContent,
  DropdownMenuPortal,
  DropdownMenuRoot,
  DropdownMenuTrigger,
} from 'reka-ui'
import {
  Comment,
  computed,
  Fragment,
  onMounted,
  onUpdated,
  ref,
  useAttrs,
  useSlots,
  watch,
} from 'vue'
function hasVisibleVnodes(vnodes) {
  return vnodes.some((vnode) => {
    if (vnode.type === Comment) return false
    if (vnode.type === Fragment) {
      return Array.isArray(vnode.children) && hasVisibleVnodes(vnode.children)
    }
    return true
  })
}

export default {
  name: 'DropdownButton',
  components: {
    DropdownMenuRoot,
    DropdownMenuTrigger,
    DropdownMenuPortal,
    DropdownMenuContent,
  },
  inheritAttrs: false,
  props: {
    label: { type: String, default: '' },
    icon: { type: [String, Array], default: null },
    variant: { type: String, default: null },
    size: { type: String, default: null },
    outlined: { type: Boolean, default: false },
    compact: { type: String, default: 'never' },
    loading: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    showArrow: { type: Boolean, default: true },
    hideWhenEmpty: { type: Boolean, default: false },
    fitContent: { type: Boolean, default: false },
    matchTriggerWidth: { type: Boolean, default: false },
    contentClass: { type: String, default: null },
    align: {
      type: String,
      default: 'start',
      validator: (v) => ['start', 'center', 'end'].includes(v),
    },
    side: {
      type: String,
      default: 'bottom',
      validator: (v) => ['top', 'bottom', 'left', 'right'].includes(v),
    },
  },
  emits: ['open', 'close'],
  setup(props, { emit, expose }) {
    const slots = useSlots()
    const attrs = useAttrs()
    const hasContent = ref(true)

    function checkContent() {
      hasContent.value = !!slots.default && hasVisibleVnodes(slots.default())
    }

    onMounted(checkContent)
    onUpdated(checkContent)

    const isOpen = ref(false)

    const arrowIcon = computed(() => {
      if (!props.showArrow) return null
      return isOpen.value
        ? 'fa-solid fa-angle-up fa-sm'
        : 'fa-solid fa-angle-down fa-sm'
    })

    const buttonProps = computed(() => ({
      label: props.label,
      icon: props.icon,
      variant: props.variant,
      size: props.size,
      outlined: props.outlined,
      compact: props.compact,
      loading: props.loading,
      disabled: props.disabled,
      rightIcon: arrowIcon.value,
      ...attrs,
    }))

    watch(isOpen, (open) => {
      if (open) checkContent()
      emit(open ? 'open' : 'close')
    })

    const open = () => {
      isOpen.value = true
    }
    const close = () => {
      isOpen.value = false
    }
    const toggle = () => {
      isOpen.value = !isOpen.value
    }

    // Auto-close when a link or an explicit close-marker inside the dropdown is
    // clicked. Slot content uses plain <a>/<router-link>/<button> tags, not
    // reka-ui DropdownMenuItem (which would auto-close via @select); add
    // data-dd-close to any element that should dismiss the panel on click.
    const handleContentClick = (e) => {
      if (e.target.closest('a, .dd-item, [data-dd-close]')) {
        isOpen.value = false
      }
    }

    expose({ open, close, toggle })

    return {
      isOpen,
      hasContent,
      arrowIcon,
      buttonProps,
      open,
      close,
      toggle,
      handleContentClick,
    }
  },
}
</script>

<!-- Portal styles in ui-elements.scss -->
